// web/src/scene/rtls/RescueChamberMarkers.tsx
//
// Web variant of webview/src/rtls/RescueChamberMarkers.tsx. Identical visual
// identity (rounded square + medical cross, status-colored border, occupancy
// in the label) but reads from Redux directly — no WebView bridge.
//
// TagBoard owns chamber CRUD; RTLS subscribes via postgres_changes in
// rtlsService.ts and Redux populates state.rtls.rescueChambers automatically.

import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useAppSelector } from '../../app/useAppSelector'
import { useMarkerActions } from './useMarkerActions'
import { MarkerHoverLabel } from './MarkerHoverLabel'
import type { ChamberStatus, RescueChamber } from '@shared/types/rtlsTypes'

const HOVER_DELAY_MS = 200

const CHAMBER_STATUS_LABEL: Record<ChamberStatus, string> = {
  available: 'Available',
  near_capacity: 'Near capacity',
  full: 'Full',
  maintenance: 'Maintenance',
}

const STATUS_COLOR: Record<ChamberStatus, string> = {
  available: '#22c55e',     // green
  near_capacity: '#f59e0b', // amber
  full: '#ef4444',          // red
  maintenance: '#9ca3af',   // gray
}

const MARKER_SIZE = 12
const MARKER_Z_OFFSET = 1
const LABEL_Z_OFFSET = MARKER_Z_OFFSET + MARKER_SIZE / 2 + 3
const LABEL_HEIGHT = 4.5

function drawChamberTexture(status: ChamberStatus): HTMLCanvasElement {
  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const cx = size / 2
  const cy = size / 2
  const half = size * 0.40
  const radius = size * 0.10

  ctx.clearRect(0, 0, size, size)

  ctx.save()
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
  ctx.shadowBlur = 6
  ctx.shadowOffsetY = 2
  ctx.beginPath()
  ctx.roundRect(cx - half, cy - half, half * 2, half * 2, radius)
  ctx.fillStyle = '#1E2A3A'
  ctx.fill()
  ctx.restore()

  ctx.beginPath()
  ctx.roundRect(cx - half, cy - half, half * 2, half * 2, radius)
  ctx.strokeStyle = STATUS_COLOR[status]
  ctx.lineWidth = size * 0.045
  ctx.lineJoin = 'round'
  ctx.stroke()

  // Medical cross
  const crossArm = size * 0.20
  const crossThickness = size * 0.07
  ctx.fillStyle = STATUS_COLOR[status]
  ctx.beginPath()
  ctx.roundRect(cx - crossThickness / 2, cy - crossArm, crossThickness, crossArm * 2, crossThickness / 2)
  ctx.fill()
  ctx.beginPath()
  ctx.roundRect(cx - crossArm, cy - crossThickness / 2, crossArm * 2, crossThickness, crossThickness / 2)
  ctx.fill()

  // Status corner dot
  const cornerX = cx + half * 0.75
  const cornerY = cy + half * 0.75
  ctx.fillStyle = STATUS_COLOR[status]
  ctx.beginPath()
  ctx.arc(cornerX, cornerY, size * 0.055, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = '#1E2A3A'
  ctx.lineWidth = size * 0.012
  ctx.stroke()

  return canvas
}

const chamberTextureCache = new Map<ChamberStatus, THREE.Texture>()

function getCachedChamberTexture(status: ChamberStatus): THREE.Texture {
  const cached = chamberTextureCache.get(status)
  if (cached) return cached
  const canvas = drawChamberTexture(status)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  tex.generateMipmaps = false
  tex.needsUpdate = true
  chamberTextureCache.set(status, tex)
  return tex
}

function clearChamberTextures(): void {
  chamberTextureCache.forEach(t => t.dispose())
  chamberTextureCache.clear()
}

function buildLabelTexture(
  name: string,
  occupancy: string,
): { texture: THREE.Texture; width: number } {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  const s = 4
  const fontSize = 20 * s
  const subFontSize = 14 * s
  const paddingH = 16 * s
  const paddingV = 10 * s

  ctx.font = `700 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`
  const nameW = ctx.measureText(name).width
  ctx.font = `500 ${subFontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`
  const occW = ctx.measureText(occupancy).width
  const innerW = Math.max(nameW, occW)
  const totalW = paddingH + innerW + paddingH
  const totalH = fontSize + subFontSize * 1.2 + paddingV * 2
  const borderRadius = 12 * s

  canvas.width = totalW
  canvas.height = totalH

  ctx.save()
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
  ctx.shadowBlur = 8 * s
  ctx.shadowOffsetY = 2 * s
  ctx.beginPath()
  ctx.roundRect(0, 0, totalW, totalH, borderRadius)
  ctx.fillStyle = 'rgba(30, 36, 51, 0.88)'
  ctx.fill()
  ctx.restore()

  ctx.beginPath()
  ctx.roundRect(0, 0, totalW, totalH, borderRadius)
  ctx.strokeStyle = 'rgba(75, 85, 99, 0.5)'
  ctx.lineWidth = 1.5 * s
  ctx.stroke()

  ctx.fillStyle = '#FFFFFF'
  ctx.font = `700 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.fillText(name, totalW / 2, paddingV)

  ctx.fillStyle = 'rgba(229, 231, 235, 0.9)'
  ctx.font = `500 ${subFontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`
  ctx.fillText(occupancy, totalW / 2, paddingV + fontSize + 4 * s)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  tex.generateMipmaps = false
  tex.needsUpdate = true
  return { texture: tex, width: LABEL_HEIGHT * (totalW / totalH) }
}

const ChamberSprite: React.FC<{ chamber: RescueChamber }> = React.memo(({ chamber }) => {
  const { onMarkerDoubleClick } = useMarkerActions()
  const [hovered, setHovered] = useState(false)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const bodyTexture = useMemo(
    () => getCachedChamberTexture(chamber.status),
    [chamber.status],
  )

  const bodyMaterial = useMemo(() => {
    const mat = new THREE.SpriteMaterial({
      map: bodyTexture,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
    })
    mat.toneMapped = false
    return mat
  }, [bodyTexture])

  const { labelMaterial, labelWidth } = useMemo(() => {
    const occupancy = `${chamber.currentOccupancy}/${chamber.capacity}`
    const { texture, width } = buildLabelTexture(chamber.name, occupancy)
    const mat = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
    })
    mat.toneMapped = false
    return { labelMaterial: mat, labelWidth: width }
  }, [chamber.name, chamber.currentOccupancy, chamber.capacity])

  const handlePointerEnter = () => {
    document.body.style.cursor = 'pointer'
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = setTimeout(() => setHovered(true), HOVER_DELAY_MS)
  }
  const handlePointerLeave = () => {
    document.body.style.cursor = 'default'
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    setHovered(false)
  }
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    document.body.style.cursor = 'default'
  }, [])

  const handleDoubleClick = (e: any) => {
    e.stopPropagation()
    onMarkerDoubleClick(chamber.position)
  }

  const occupancyLabel = `${CHAMBER_STATUS_LABEL[chamber.status]} · ${chamber.currentOccupancy}/${chamber.capacity}`

  return (
    <group position={[chamber.position.x, chamber.position.y, chamber.position.z]}>
      <sprite
        material={bodyMaterial}
        scale={[MARKER_SIZE, MARKER_SIZE, 1]}
        position={[0, 0, MARKER_Z_OFFSET]}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onDoubleClick={handleDoubleClick}
      />
      <sprite
        material={labelMaterial}
        scale={[labelWidth, LABEL_HEIGHT, 1]}
        position={[0, 0, LABEL_Z_OFFSET]}
      />
      <MarkerHoverLabel
        visible={hovered}
        position={[0, 0, MARKER_Z_OFFSET + MARKER_SIZE]}
        primary={chamber.name}
        secondary={`Rescue chamber · ${occupancyLabel}`}
      />
    </group>
  )
})

ChamberSprite.displayName = 'ChamberSprite'

export const RescueChamberMarkers: React.FC = () => {
  const chambersRecord = useAppSelector(s => s.rtls.rescueChambers)
  const filterLevel    = useAppSelector(s => s.rtls.filterLevel)
  const panelFilter    = useAppSelector(s => s.rtls.panelFilter)

  const chambers = useMemo(() => {
    const all = Object.values(chambersRecord)
    if (filterLevel === 'all') return all
    return all.filter(c => c.level === filterLevel)
  }, [chambersRecord, filterLevel])

  useEffect(() => {
    return () => {
      clearChamberTextures()
    }
  }, [])

  // Hide entirely unless the panel is set to All or Chambers.
  if (panelFilter !== 'all' && panelFilter !== 'chamber') return null

  return (
    <group>
      {chambers.map(c => (
        <ChamberSprite key={c.id} chamber={c} />
      ))}
    </group>
  )
}

RescueChamberMarkers.displayName = 'RescueChamberMarkers'
